import { useMemo, useEffect, useRef } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three/webgpu";
import { uniform, vec2, float, sin, cos, screenUV, atan, dot, fract, floor, mix, clamp, smoothstep, add, sub, Fn, OnMaterialUpdate } from "three/tsl";
import { MeshPhysicalNodeMaterial } from "three/webgpu";
import gsap from "gsap";

export function TransitionMaterial({
  transitionColor = "#ff0000",
  duration = 0.95,
  center = [0.5, 0.5],
  animate = "auto", // kept for compatibility, now using GPU time-based progress
  map = null,
  normalMap = null,
  normalScale = [1, 1],
  ...props
}) {
  const { size, clock } = useThree();
  const animationIdRef = useRef(null); // Reference to store GSAP animation ID

  // Animation state management
  // This system allows smooth color transitions without restarting animations
  // When a new color is requested during an ongoing animation, it's queued
  // and will be processed after the current animation completes
  const isAnimating = useRef(false);
  const colorQueue = useRef([]);
  const currentTransition = useRef(null);

  const prevColor = useRef(new THREE.Color("#ffffff"));
  const newColor = useRef(new THREE.Color(transitionColor));

  // Function to process color queue
  const processColorQueue = () => {
    if (colorQueue.current.length === 0 || isAnimating.current) return;

    const nextColor = colorQueue.current.shift();
    if (!nextColor) return;

    isAnimating.current = true;

    // Update colors for the transition
    newColor.current.set(nextColor);
    if (material) {
      material.userData.transitionColor = nextColor;
      material.userData.prevColor = prevColor.current;
    }

    // Start animation
    animationIdRef.current = gsap.fromTo(
      material?.userData || {},
      { progress: 0 },
      {
        progress: 1,
        duration: duration,
        ease: "power1.out",
        onComplete: () => {
          // Update prevColor to current newColor for next transition
          prevColor.current.copy(newColor.current);
          if (material) {
            material.userData.prevColor = prevColor.current;
          }
          isAnimating.current = false;

          // Process next color in queue
          processColorQueue();
        },
      }
    );
  };

  // Function to add color to queue
  const addColorToQueue = (color) => {
    // If not animating, start immediately
    if (!isAnimating.current) {
      colorQueue.current = [color]; // Clear queue and add new color
      processColorQueue();
    } else {
      // If animating, add to queue (replace last if it's the same color)
      if (colorQueue.current.length > 0 && colorQueue.current[colorQueue.current.length - 1] === color) {
        return; // Don't add duplicate colors
      }
      // Keep only the last 2 colors to avoid long queues
      if (colorQueue.current.length >= 2) {
        colorQueue.current = [colorQueue.current[colorQueue.current.length - 1], color];
      } else {
        colorQueue.current.push(color);
      }
    }
  };

  // Create the TSL function with events - this will be stable across re-renders
  const transitionFn = useMemo(() => {
    return Fn(() => {
      // uniforms - these will be updated via events, not recreated
      const uProgress = uniform(0.0);
      const uPrevColor = uniform(prevColor.current);
      const uNewColor = uniform(newColor.current);
      const uResolution = uniform(new THREE.Vector2(size.width * Math.min(window.devicePixelRatio, 2), size.height * Math.min(window.devicePixelRatio, 2)));
      const uCenter = uniform(new THREE.Vector2(0.5, 0.5)); // screen-space center

      // Use OnMaterialUpdate to update uniforms when material is rendered
      OnMaterialUpdate(({ material }) => {
        // Update colors from material userData or props
        if (material.userData.transitionColor) {
          newColor.current.set(material.userData.transitionColor);
          uNewColor.value.copy(newColor.current);
        }
        if (material.userData.prevColor) {
          prevColor.current.set(material.userData.prevColor);
          uPrevColor.value.copy(prevColor.current);
        }
        if (material.userData.progress !== undefined) {
          uProgress.value = material.userData.progress;
        }
        if (material.userData.center) {
          uCenter.value.set(material.userData.center[0], material.userData.center[1]);
        }
        if (material.userData.resolution) {
          uResolution.value.copy(material.userData.resolution);
        }
      });

      return createTransitionNode(uProgress, uPrevColor, uNewColor, uResolution, uCenter, duration);
    });
  }, [size.width, size.height, duration]); // Only recreate when size or duration changes

  // Helper function to create the transition node
  const createTransitionNode = (uProgress, uPrevColor, uNewColor, uResolution, uCenter, duration) => {
    // ----------------------------
    // GLSL helper conversions → TSL
    // ----------------------------
    const rand2 = (n) => fract(sin(dot(n, vec2(12.9898, 4.1414))).mul(43758.5453));

    const noise = (p) => {
      const ip = floor(p);
      const u = p.sub(ip);
      const u2 = u.mul(u).mul(sub(float(3.0), u.mul(2.0)));
      const mix1 = mix(rand2(ip), rand2(ip.add(vec2(1.0, 0.0))), u2.x);
      const mix2 = mix(rand2(ip.add(vec2(0.0, 1.0))), rand2(ip.add(vec2(1.0, 1.0))), u2.x);
      return mix(mix1, mix2, u2.y);
    };

    const fbm = (p, octaves) => {
      let n = float(0.0);
      let a = float(1.0);
      let norm = float(0.0);
      let pp = p;
      for (let i = 0; i < octaves; i++) {
        n = add(n, noise(pp).mul(a));
        norm = add(norm, a);
        pp = pp.mul(2.0);
        a = a.mul(0.5);
      }
      return n.div(norm);
    };

    // ----------------------------
    // Splash logic em nodes (screen-space)
    // ----------------------------
    const suv = screenUV; // 0..1 across the framebuffer
    const centeredUV = suv.sub(uCenter); // center controlled by uniform
    const aspect = uResolution.y.div(uResolution.x);
    const uvScaled = vec2(centeredUV.x, centeredUV.y.mul(aspect));

    const angle = atan(uvScaled.y, uvScaled.x).add(fbm(uvScaled.mul(4.0), 2).mul(0.5));
    const p = vec2(cos(angle), sin(angle));

    // Improved progress calculation to ensure full screen coverage
    let t = uProgress;
    // Use a more aggressive curve to ensure full coverage
    t = t.mul(t).mul(1.2); // Increased multiplier for better coverage
    const tSafe = clamp(t, 0.001, 1.0);

    // Calculate distance from center with proper scaling
    let l = dot(uvScaled.div(tSafe), uvScaled.div(tSafe));
    // Add noise for organic edge but reduce its impact
    l = sub(l, fbm(uvScaled.mul(30.0), 8).mul(0.3).sub(0.15));

    // Ensure the transition covers the entire screen by adjusting the threshold
    const ink = fbm(p.mul(16.0), 1).add(1.2).sub(l); // Reduced threshold for better coverage
    const inkClamped = clamp(ink, 0.0, 1.0);

    // Add a fallback to ensure 100% coverage at progress = 1
    const finalProgress = mix(inkClamped, float(1.0), smoothstep(0.95, 1.0, uProgress));

    return mix(uPrevColor, uNewColor, finalProgress);
  };

  // Create material with useMemo for performance - only recreate when necessary
  const material = useMemo(() => {
    // Ensure normalScale is a Vector2
    let normalScaleVec2 = new THREE.Vector2(1, 1);
    if (normalScale) {
      if (Array.isArray(normalScale)) {
        normalScaleVec2 = new THREE.Vector2(normalScale[0], normalScale[1]);
      } else if (normalScale instanceof THREE.Vector2) {
        normalScaleVec2 = normalScale;
      } else if (typeof normalScale === "number") {
        normalScaleVec2 = new THREE.Vector2(normalScale, normalScale);
      }
    }

    const mat = new MeshPhysicalNodeMaterial({
      colorNode: transitionFn(),
      map: map,
      normalMap: normalMap,
      normalScale: normalScaleVec2,
      ...props,
    });

    // Store initial values in userData for the OnMaterialUpdate event
    mat.userData.transitionColor = transitionColor;
    mat.userData.prevColor = prevColor.current;
    mat.userData.progress = 0;
    mat.userData.center = center;
    mat.userData.resolution = new THREE.Vector2(size.width * Math.min(window.devicePixelRatio, 1.5), size.height * Math.min(window.devicePixelRatio, 1.5));

    return mat;
  }, [size.width, size.height]);

  // Handle color changes and add to queue
  useEffect(() => {
    if (!material) return;

    // Add the new color to the queue
    addColorToQueue(transitionColor);
  }, [transitionColor, duration, material]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationIdRef.current) {
        gsap.killTweensOf(material?.userData);
      }
      // Reset animation state
      isAnimating.current = false;
      colorQueue.current = [];
    };
  }, [material]);

  // Update resolution when size changes
  useEffect(() => {
    if (material) {
      material.userData.resolution.set(size.width * Math.min(window.devicePixelRatio, 1.5), size.height * Math.min(window.devicePixelRatio, 1.5));
    }
  }, [size, material]);

  // Update center when center changes
  useEffect(() => {
    if (material) {
      material.userData.center = center;
    }
  }, [center, material]);

  // Update normalScale when it changes
  useEffect(() => {
    if (material && normalScale) {
      // Convert normalScale to Vector2 if needed
      let normalScaleVec2 = new THREE.Vector2(1, 1);
      if (Array.isArray(normalScale)) {
        normalScaleVec2 = new THREE.Vector2(normalScale[0], normalScale[1]);
      } else if (normalScale instanceof THREE.Vector2) {
        normalScaleVec2 = normalScale;
      } else if (typeof normalScale === "number") {
        normalScaleVec2 = new THREE.Vector2(normalScale, normalScale);
      }

      // Update normalScale using copy method if available
      if (material.normalScale && typeof material.normalScale.copy === "function") {
        material.normalScale.copy(normalScaleVec2);
      } else {
        material.normalScale = normalScaleVec2;
      }
      material.needsUpdate = true;
    }
  }, [normalScale, material]);

  // Force redraws during animation for better performance
  //   useFrame((state) => {
  //     if (
  //       material?.userData?.progress !== undefined &&
  //       material.userData.progress < 1
  //     ) {
  //       state.invalidate();
  //     }
  //   });

  if (!material) return null;

  return <primitive attach="material" {...props} object={material} />;
}
